import { type FormIndex } from "./form-index";
import { formatAnswer } from "./format";
import { toText } from "./expression";

export interface TemplateContext {
  /** Antworten nach Feld-ID */
  answers: Record<string, unknown>;
  other?: Record<string, string>;
  vars: Record<string, number | string>;
  hidden: Record<string, string>;
}

export type TemplateRef = { scope: "field" | "var" | "hidden"; name: string };

/**
 * Platzhalter: `{{field:key}}`, `{{var:name}}`, `{{hidden:name}}`,
 * optional mit Ersatztext: `{{field:vorname|du}}`.
 */
const PLACEHOLDER = /\{\{\s*(field|var|hidden)\s*:\s*([a-z][a-z0-9_]*)\s*(?:\|([^}]*))?\}\}/g;

export function parseTemplateRefs(text: string): TemplateRef[] {
  return [...text.matchAll(PLACEHOLDER)].map((m) => ({ scope: m[1] as TemplateRef["scope"], name: m[2]! }));
}

const numberFmt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

/**
 * Ersetzt Platzhalter durch formatierte Werte. Gibt reinen Text zurück –
 * HTML-Escaping ist Aufgabe des Renderers. Mit `encode: "url"` für Weiterleitungs-URLs.
 */
export function renderTemplate(
  text: string,
  idx: FormIndex,
  ctx: TemplateContext,
  opts: { encode?: "url" } = {},
): string {
  return text.replace(PLACEHOLDER, (_all, scope: string, name: string, fallback?: string) => {
    let out = "";
    if (scope === "field") {
      const field = idx.fieldByKey.get(name);
      if (field) out = formatAnswer(field, ctx.answers[field.id], ctx.other?.[field.id]);
    } else if (scope === "var") {
      const v = ctx.vars[name];
      out = typeof v === "number" ? numberFmt.format(v) : toText(v);
    } else {
      out = toText(ctx.hidden[name]);
    }
    if (out === "" && fallback !== undefined) out = fallback.trim();
    return opts.encode === "url" ? encodeURIComponent(out) : out;
  });
}
